import React from 'react';

import { Typography } from '@mui/material';
import StatusIcon from '@sharedCR/common/StatusIcon';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';

import { fixedTwoFractionDigitsCurrencyOptions } from 'constants/loanApplication';
import { typeOfCreditMap } from 'constants/options';
import { StatusEnum } from 'constants/status';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';
import { formatNumberToCurrencyString } from 'utils/number';

import BorrowersNames from '../../financialInfo/FinancialInfoBody/BorrowersNames';

import { emptyMessageCellStyles } from './SecondaryFinancing.styles';

export const additionalMortgagesConfig = {
  creditName: {
    label: 'Credit Name',
    width: '16%',
    content: ({ creditName }) =>
      creditName?.content ?? (
        <Typography data-truncated className="truncate" variant="regular14">
          {creditName}
        </Typography>
      ),
    colSpan: ({ creditName }) => creditName?.colSpan,
    customStyles: ({ creditName }) => creditName?.customStyles,
  },
  amount: {
    label: 'Amount',
    align: HORIZONTAL_ALIGNMENT.RIGHT,
    width: '11%',
    content: ({ amount }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {formatNumberToCurrencyString(amount ?? 0, fixedTwoFractionDigitsCurrencyOptions)}
      </Typography>
    ),
  },
  helocHighLimit: {
    label: 'HELOC High Limit',
    width: '11%',
    align: HORIZONTAL_ALIGNMENT.RIGHT,
    content: ({ helocHighLimit }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {helocHighLimit
          ? formatNumberToCurrencyString(helocHighLimit, fixedTwoFractionDigitsCurrencyOptions)
          : '-'}
      </Typography>
    ),
  },
  monthlyPayment: {
    label: 'Monthly Payment',
    align: HORIZONTAL_ALIGNMENT.RIGHT,
    width: '11%',
    content: ({ monthlyPayment }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {formatNumberToCurrencyString(monthlyPayment ?? 0, fixedTwoFractionDigitsCurrencyOptions)}
      </Typography>
    ),
  },
  lienPriority: {
    label: 'Lien Priority',
    width: '11%',
    content: ({ lienPriority }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {lienPriority}
      </Typography>
    ),
  },
  sourceOfFunds: {
    label: 'Source of Funds',
    width: '10%',
    content: ({ sourceOfFunds }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {sourceOfFunds}
      </Typography>
    ),
  },
  paymentExcluded: {
    label: 'Payment Excluded',
    width: '10%',
    align: HORIZONTAL_ALIGNMENT.CENTER,
    content: ({ paymentExcluded }) => (
      <StatusIcon status={paymentExcluded ? StatusEnum.SUCCESS : StatusEnum.ERROR} />
    ),
  },
  subordinate: {
    label: 'Subordinate',
    width: '10%',
    align: HORIZONTAL_ALIGNMENT.CENTER,
    content: ({ subordinate }) => (
      <StatusIcon status={subordinate ? StatusEnum.SUCCESS : StatusEnum.ERROR} />
    ),
  },
  monthsDeferred: {
    label: 'Months Deferred',
    width: '10%',
    content: ({ monthsDeferred }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {monthsDeferred}
      </Typography>
    ),
  },
};

export const additionalMortgagesNotFoundTableData = [
  {
    creditName: {
      position: 1,
      customStyles: emptyMessageCellStyles,
      colSpan: 10,
      content: <NoAvailableMessage className="empty-message-row" message="No results" />,
    },
  },
];

export const liabilitiesConfig = {
  ownersInfo: {
    label: 'Borrower',
    width: '23.2%',
    colSpan: ({ ownersInfo }) => ownersInfo?.colSpan,
    customStyles: ({ ownersInfo }) => ownersInfo?.customStyles,
    content: ({
      ownersInfo,
      modifiedFields: rowModifiedFields,
      pendingChangesActionType: rowPendingChangesActionType,
    }) =>
      ownersInfo?.content ?? (
        <BorrowersNames
          ownersInfo={ownersInfo}
          rowModifiedFields={rowModifiedFields}
          rowPendingChangesActionType={rowPendingChangesActionType}
        />
      ),
    fieldName: 'ownersFullNames',
  },
  liabilityAccountType: {
    label: 'Type',
    width: '11.8%',
    content: ({ liabilityAccountType, liabilityExpenseType }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {typeOfCreditMap[liabilityAccountType || liabilityExpenseType]?.name ?? '-'}
      </Typography>
    ),
  },
  creditorName: {
    label: 'Creditor',
    width: '11.5%',
    content: ({ creditorName }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {creditorName || '-'}
      </Typography>
    ),
  },
  unpaidBalance: {
    label: 'Unpaid Balance',
    width: '11.5%',
    align: 'right',
    content: ({ unpaidBalance }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {formatNumberToCurrencyString(unpaidBalance)}
      </Typography>
    ),
  },
  monthlyPayment: {
    label: 'Payment/Mo',
    align: 'right',
    width: '10.5%',
    content: ({ monthlyPayment, remainingMonths }) => {
      const payment = formatNumberToCurrencyString(Number(monthlyPayment));

      return (
        <Typography data-truncated className="truncate" variant="regular14">
          {remainingMonths ? `${payment}/${remainingMonths}` : payment}
        </Typography>
      );
    },
  },
  remainingPayments: {
    label: 'Remaining Payments',
    width: '10.5%',
    align: 'center',
    content: ({ remainingPayments }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {remainingPayments || '-'}
      </Typography>
    ),
  },
  payoffRequired: {
    label: 'Payoff Required',
    width: '10.5%',
    align: 'center',
    content: ({ payoffRequired }) => (
      <StatusIcon status={payoffRequired ? StatusEnum.SUCCESS : StatusEnum.ERROR} />
    ),
  },
  exclude: {
    label: 'Exclude',
    width: '10.5%',
    align: 'center',
    content: ({ exclude }) => (
      <StatusIcon status={exclude ? StatusEnum.SUCCESS : StatusEnum.ERROR} />
    ),
  },
};

export const liabilitiesNotFoundTableData = [
  {
    ownersInfo: {
      position: 1,
      customStyles: emptyMessageCellStyles,
      colSpan: 8,
      content: <NoAvailableMessage className="empty-message-row" message="No results" />,
    },
  },
];
